import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import api from '../../services/api';

interface SyncRun {
  id: number;
  mode: 'dry_run' | 'manual' | 'scheduled';
  status: string;
  requested_count: number;
  processed_count: number;
  created_count: number;
  updated_count: number;
  skipped_count: number;
  error_count: number;
  started_at: string;
  finished_at: string | null;
}

interface ErpStatus {
  configured: boolean;
  baseUrl: string | null;
  writesEnabled: boolean;
  maxBatch: number;
  cronSchedules?: string[];
  recentRuns: SyncRun[];
}

interface PreviewResult {
  runId: number;
  status: string;
  processedCount: number;
  createdCount: number;
  updatedCount: number;
  deactivatedCount?: number;
  skippedCount: number;
  errorCount: number;
  details: Array<{ sku: string; action: string; status: string; message?: string | null }>;
}

const Container = styled.section`
  max-width: 980px;
  margin: 0 auto;
  padding: 24px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
  padding: 24px;
  margin-bottom: 20px;
`;

const Status = styled.span<{ $ok: boolean }>`
  display: inline-block;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $ok }) => ($ok ? '#166534' : '#991b1b')};
  background: ${({ $ok }) => ($ok ? '#dcfce7' : '#fee2e2')};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 104px;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 12px;
  font: inherit;
  resize: vertical;
`;

const Button = styled.button<{ $primary?: boolean; $variant?: 'success' | 'danger' | 'warning' }>`
  border: 0;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;

  ${({ $variant, $primary }) => {
    if ($variant === 'success') {
      return `
        color: #fff;
        background: #16a34a;
        &:hover:not(:disabled) { background: #15803d; }
      `;
    }
    if ($variant === 'danger') {
      return `
        color: #fff;
        background: #dc2626;
        &:hover:not(:disabled) { background: #b91c1c; }
      `;
    }
    if ($primary) {
      return `
        color: #fff;
        background: #1e3a8a;
        &:hover:not(:disabled) { background: #172554; }
      `;
    }
    return `
      color: #1e3a8a;
      background: #e8eefc;
      &:hover:not(:disabled) { background: #dbeafe; }
    `;
  }}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const ScheduleBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #1e293b;
  padding: 6px 14px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;

  button {
    background: transparent;
    border: none;
    color: #64748b;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 0;
    margin-left: 2px;
    display: flex;
    align-items: center;

    &:hover {
      color: #ef4444;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th, td {
    text-align: left;
    padding: 10px 8px;
    border-bottom: 1px solid #e5e7eb;
  }
`;

function parseSkus(value: string) {
  return [...new Set(value.split(/[\s,;]+/).map((item) => item.trim()).filter(Boolean))];
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString('es-PE') : '-';
}

function formatTime12h(time24: string) {
  const [h, m] = time24.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return time24;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  const minute = String(m).padStart(2, '0');
  return `${hour12}:${minute} ${ampm}`;
}

export default function ErpSyncTab() {
  const [status, setStatus] = useState<ErpStatus | null>(null);
  const [skus, setSkus] = useState('21333');
  const [busy, setBusy] = useState(false);
  const [massBusy, setMassBusy] = useState(false);
  const [result, setResult] = useState<PreviewResult | null>(null);

  // Horarios de sincronización automática (Cron)
  const [schedules, setSchedules] = useState<string[]>(['06:00', '13:30', '19:00']);
  const [newTime, setNewTime] = useState('06:00');
  const [savingSchedules, setSavingSchedules] = useState(false);
  const [schedulesChanged, setSchedulesChanged] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const response = await api.get<ErpStatus>('/products/erp/status');
      setStatus(response.data);
      if (response.data.cronSchedules && Array.isArray(response.data.cronSchedules)) {
        setSchedules(response.data.cronSchedules);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'No se pudo cargar el estado del ERP.');
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Ejecución masiva de todo el catálogo desde Odoo
  const executeMassSync = async () => {
    if (!window.confirm('¿Deseas iniciar la sincronización masiva de todo el catálogo desde Odoo?\n\nSe descargarán todos los productos destacados para la web, se actualizarán precios/stock y se darán de alta los productos nuevos.')) {
      return;
    }

    setMassBusy(true);
    try {
      const response = await api.post<PreviewResult>('/products/erp/sync-all');
      setResult(response.data);
      toast.success(`¡Sincronización masiva completada! ${response.data.processedCount} producto(s) procesados.`);
      await loadStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'No se pudo completar la sincronización masiva.');
    } finally {
      setMassBusy(false);
    }
  };

  // Agregar horario a la lista
  const handleAddSchedule = () => {
    if (!newTime || !/^([01]\d|2[0-3]):([0-5]\d)$/.test(newTime)) {
      toast.error('Selecciona una hora válida (HH:MM).');
      return;
    }
    if (schedules.includes(newTime)) {
      toast.info('Ese horario ya está configurado.');
      return;
    }
    const updated = [...schedules, newTime].sort();
    setSchedules(updated);
    setSchedulesChanged(true);
  };

  // Quitar horario de la lista
  const handleRemoveSchedule = (timeToRemove: string) => {
    if (schedules.length <= 1) {
      toast.warn('Debe existir al menos un horario de sincronización configurado.');
      return;
    }
    const updated = schedules.filter(t => t !== timeToRemove);
    setSchedules(updated);
    setSchedulesChanged(true);
  };

  // Guardar horarios en el backend
  const handleSaveSchedules = async () => {
    setSavingSchedules(true);
    try {
      await api.put('/products/erp/schedules', { schedules });
      toast.success('¡Horarios de sincronización automática actualizados con éxito!');
      setSchedulesChanged(false);
      await loadStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar los horarios de sincronización.');
    } finally {
      setSavingSchedules(false);
    }
  };

  // Ejecución individual / prueba de SKUs
  const execute = async (write: boolean) => {
    const selectedSkus = parseSkus(skus);
    if (!selectedSkus.length) {
      toast.error('Ingresa al menos un SKU.');
      return;
    }
    if (status && selectedSkus.length > status.maxBatch) {
      toast.error(`El máximo por lote es ${status.maxBatch} SKU.`);
      return;
    }
    if (write && !window.confirm('¿Deseas sincronizar inmediatamente estos SKU desde Odoo y actualizar sus precios/stock en la tienda web?')) return;

    setBusy(true);
    try {
      const response = await api.post<PreviewResult>(
        write ? '/products/erp/sync' : '/products/erp/preview',
        write ? { skus: selectedSkus, confirmation: 'SYNC', force: true } : { skus: selectedSkus },
      );
      setResult(response.data);
      toast.success(write ? '¡Precios y stock sincronizados y guardados en la web!' : 'Prueba en seco completada (no se guardaron cambios).');
      await loadStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'No se pudo completar la operación ERP.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container>
      <h2>Sincronización ERP (Odoo)</h2>
      <p>Sincroniza precios, stock y catálogo en vivo entre Odoo ERP y la tienda web.</p>

      {/* Tarjeta 1: Estado de Conexión */}
      <Card>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0 }}>Conexión ERP</h3>
            <p style={{ marginBottom: 0 }}>{status?.baseUrl || 'URL ERP no configurada'}</p>
          </div>
          <Status $ok={Boolean(status?.configured)}>{status?.configured ? 'Conectado a Odoo' : 'Falta token válido'}</Status>
        </div>
      </Card>

      {/* Tarjeta 2: Sincronización Masiva Total */}
      <Card style={{ borderLeft: '4px solid #16a34a' }}>
        <h3 style={{ marginTop: 0, color: '#166534' }}>🌐 Sincronización Masiva del Catálogo Completo</h3>
        <p style={{ color: '#475569', lineHeight: 1.5 }}>
          Descarga y sincroniza automáticamente <strong>todos los productos habilitados para la web</strong> desde Odoo (paginación de 100 en 100).
          Crea productos nuevos en la web, actualiza precios y stock, y desactiva productos retirados en Odoo.
        </p>
        <div style={{ marginTop: 16 }}>
          <Button
            $variant="success"
            onClick={executeMassSync}
            disabled={massBusy || busy || !status?.configured}
            style={{ fontSize: '15px', padding: '12px 22px' }}
          >
            {massBusy ? '⏳ Sincronizando catálogo completo desde Odoo…' : '🚀 Sincronizar Todo el Catálogo desde Odoo Ahora'}
          </Button>
        </div>
      </Card>

      {/* Tarjeta 3: Horarios de Actualización Automática (Cron) */}
      <Card style={{ borderLeft: '4px solid #1e3a8a' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 style={{ marginTop: 0, color: '#1e3a8a' }}>⏰ Horarios de Actualización Automática (Cron)</h3>
            <p style={{ color: '#475569', margin: '4px 0 12px 0' }}>
              El servidor ejecuta la sincronización masiva automáticamente en estos horarios todos los días (Zona: America/Lima).
            </p>
          </div>
          <Status $ok={true}>Cron Activo</Status>
        </div>

        {/* Lista de horarios configurados */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '14px 0', alignItems: 'center' }}>
          {schedules.map((time) => (
            <ScheduleBadge key={time}>
              <span>⏰ {time} ({formatTime12h(time)})</span>
              <button
                type="button"
                onClick={() => handleRemoveSchedule(time)}
                title={`Eliminar horario ${time}`}
              >
                ×
              </button>
            </ScheduleBadge>
          ))}
        </div>

        {/* Controles para agregar nuevo horario */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="newScheduleInput" style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
              Nuevo horario:
            </label>
            <input
              id="newScheduleInput"
              type="time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                fontFamily: 'inherit',
              }}
            />
            <Button onClick={handleAddSchedule} style={{ padding: '8px 14px' }}>
              + Agregar
            </Button>
          </div>

          {schedulesChanged && (
            <Button
              $primary
              onClick={handleSaveSchedules}
              disabled={savingSchedules}
              style={{ padding: '8px 18px', marginLeft: 'auto' }}
            >
              {savingSchedules ? 'Guardando…' : '💾 Guardar Horarios'}
            </Button>
          )}
        </div>
      </Card>

      {/* Tarjeta 4: Prueba manual por SKU */}
      <Card>
        <h3>🔍 Sincronizar / Probar Productos Específicos por SKU</h3>
        <p>Ingresa uno o más SKU separados por coma o espacio (ej: 21333, 21508, 18884). Máximo: {status?.maxBatch || 50}.</p>
        <Textarea value={skus} onChange={(event) => setSkus(event.target.value)} aria-label="SKU ERP" placeholder="Ej: 21333" />
        <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
          <Button $primary onClick={() => execute(true)} disabled={busy || massBusy || !status?.configured}>
            {busy ? 'Sincronizando…' : '⚡ Sincronizar SKU Indicados'}
          </Button>
          <Button onClick={() => execute(false)} disabled={busy || massBusy || !status?.configured}>
            {busy ? 'Simulando…' : '🔍 Simular (Prueba en seco)'}
          </Button>
        </div>
      </Card>

      {/* Tarjeta 5: Resultado de la última ejecución */}
      {result && <Card>
        <h3>Resultado de Ejecución #{result.runId}</h3>
        <p>
          Procesados: <strong>{result.processedCount}</strong> ·
          Creados: <strong>{result.createdCount}</strong> ·
          Actualizados: <strong>{result.updatedCount}</strong> ·
          {result.deactivatedCount !== undefined && <> Desactivados: <strong>{result.deactivatedCount}</strong> ·</>}
          Omitidos: <strong>{result.skippedCount}</strong> ·
          Errores: <strong>{result.errorCount}</strong>
        </p>
        {result.details && result.details.length > 0 && (
          <Table>
            <thead><tr><th>SKU</th><th>Acción</th><th>Estado</th><th>Detalle</th></tr></thead>
            <tbody>
              {result.details.map((item, index) => (
                <tr key={`${item.sku}-${index}`}>
                  <td><strong>{item.sku}</strong></td>
                  <td>{item.action}</td>
                  <td>
                    <span style={{
                      color: item.status === 'synced' ? '#16a34a' : (item.status === 'error' ? '#dc2626' : '#64748b'),
                      fontWeight: 600,
                    }}>
                      {item.status}
                    </span>
                  </td>
                  <td>{item.message || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>}

      {/* Tarjeta 6: Historial de ejecuciones */}
      <Card>
        <h3>Últimas ejecuciones</h3>
        <Table>
          <thead><tr><th>ID</th><th>Modo</th><th>Estado</th><th>Procesados</th><th>Errores</th><th>Inicio</th></tr></thead>
          <tbody>
            {status?.recentRuns?.map((run) => (
              <tr key={run.id}>
                <td>#{run.id}</td>
                <td>{run.mode}</td>
                <td>
                  <span style={{
                    color: run.status === 'completed' ? '#16a34a' : '#d97706',
                    fontWeight: 600,
                  }}>
                    {run.status}
                  </span>
                </td>
                <td>{run.processed_count}/{run.requested_count}</td>
                <td>{run.error_count}</td>
                <td>{formatDate(run.started_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
}
